import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  assertAdminRole,
  fetchCop31Enrichment,
  repairCop31Note,
  COP31_CONCEPTS,
  type Cop31NoteFixReport,
} from "@/lib/cop31-note-health.server";

export const quickFixCop31Note = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data, context }): Promise<Cop31NoteFixReport> => {
    await assertAdminRole(context.supabase, context.userId);
    const apiKey = process.env.LOVABLE_API_KEY;
    if (!apiKey) throw new Error("LOVABLE_API_KEY is not configured");
    return repairCop31Note(data.slug, apiKey);
  });

/** Public read: AI enrichment (answer, takeaways, concepts) for one COP31 note. */
export const getCop31NoteEnrichment = createServerFn({ method: "GET" })
  .inputValidator((d: unknown) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }) => {
    const row = (await fetchCop31Enrichment([data.slug])).get(data.slug);
    return {
      summaryEn: row?.summaryEn ?? null,
      takeawaysEn: row?.takeawaysEn ?? [],
      concepts: (row?.conceptsEn ?? []).map((id) => {
        const concept = COP31_CONCEPTS.find((c) => c.id === id);
        return { id, slug: concept?.enSlug ?? id, label: concept?.labelEn ?? id };
      }),
      sources: row?.sourcesEn ?? [],
      reportLinks: row?.reportLinksEn ?? [],
    };
  });
